/**
 * shareUrl.ts
 * Encode/decode CalcInputs into a compact base64url query param `?s=...`
 * so any slider configuration can be shared as a single URL.
 *
 * Encoding strategy:
 *   [cpuCores, ramGb, computeHours, storageGb, storageTier, egressGb, invocations]
 * Tier is stored as 0|1|2 (standard|cold|archive).
 * Numbers are comma-separated, then btoa'd with URL-safe substitution.
 */

import type { CalcInputs, StorageTier } from './pricing'

const TIER_INDEX: Record<StorageTier, number> = { standard: 0, cold: 1, archive: 2 }
const INDEX_TIER: StorageTier[] = ['standard', 'cold', 'archive']
const PARAM = 's'

function toBase64Url(str: string): string {
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function fromBase64Url(b64: string): string {
  const padded = b64.replace(/-/g, '+').replace(/_/g, '/').padEnd(
    b64.length + ((4 - (b64.length % 4)) % 4),
    '=',
  )
  return atob(padded)
}

export function encodeInputs(inputs: CalcInputs): string {
  const parts = [
    inputs.cpuCores,
    inputs.ramGb,
    inputs.computeHours,
    inputs.storageGb,
    TIER_INDEX[inputs.storageTier],
    inputs.egressGb,
    inputs.invocations,
  ]
  return toBase64Url(parts.join(','))
}

export function decodeInputs(encoded: string): CalcInputs | null {
  try {
    const raw = fromBase64Url(encoded)
    const parts = raw.split(',').map(Number)
    if (parts.length !== 7 || parts.some(isNaN)) return null
    const [cpuCores, ramGb, computeHours, storageGb, tierIdx, egressGb, invocations] = parts
    const storageTier = INDEX_TIER[tierIdx] ?? 'standard'
    return { cpuCores, ramGb, computeHours, storageGb, storageTier, egressGb, invocations }
  } catch {
    return null
  }
}

/** Returns the full shareable URL for the given inputs. */
export function buildShareUrl(inputs: CalcInputs): string {
  const url = new URL(window.location.href)
  url.searchParams.set(PARAM, encodeInputs(inputs))
  // strip hash so anchor links don't interfere
  url.hash = ''
  return url.toString()
}

/**
 * Reads `?s=` from the current URL and returns decoded CalcInputs, or null
 * if the param is absent / malformed.
 */
export function readUrlInputs(): CalcInputs | null {
  const params = new URLSearchParams(window.location.search)
  const encoded = params.get(PARAM)
  if (!encoded) return null
  return decodeInputs(encoded)
}

/**
 * Pushes a new history entry with the encoded inputs in the URL, without
 * reloading the page.
 */
export function pushInputsToUrl(inputs: CalcInputs): void {
  const url = buildShareUrl(inputs)
  window.history.replaceState(null, '', url)
}
